<template>
  <div ref="chart">
    <Chart :height="height" :id="id" :option="option" :width="width"></Chart>
  </div>
</template>

<script>
import Chart from '@/components/echarts/Chart';

export default {
  name: "CabinetBarChart",
  components: { Chart },
  props: {
    data: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      id: "CabinetBarChart_" + Math.random().toString(36).substr(2, 9),
      width: '100%',
      height: '100%',
      option: {}
    };
  },
  watch: {
    data: {
      handler() {
        this.getOption();
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    this.getOption();
  },
  methods: {
    getOption() {
      if (!this.data) return;
      
      const { deviceNames, statuses, 
        m1_a_u, m1_b_u, m1_c_u, 
        m2_a_u, m2_b_u, m2_c_u, 
        thresholds, pointNames 
      } = this.data;
      
      const names = pointNames || ['A路A相电压', 'A路B相电压', 'A路C相电压', 'B路A相电压', 'B路B相电压', 'B路C相电压'];

      // 构建阈值线生成函数
      const createMarkLineData = (thresholdList, phaseColor) => {
        const markLineData = [];
        if (thresholdList && Array.isArray(thresholdList)) {
          thresholdList.forEach(t => {
            const namePrefix = t.pointName || '';
            const level = t.levelName || '';
            if (t.alarmHigh != null) {
              markLineData.push({
                yAxis: t.alarmHigh,
                name: `${namePrefix}${level}上限`,
                lineStyle: { color: t.levelColor || phaseColor || '#ff0000', type: 'dashed', width: 2 },
                label: { 
                  show: true, 
                  position: 'end',
                  // formatter: `${level}上限: {c}V`,
                  formatter: `{c}V`,
                  color: t.levelColor || phaseColor || '#ff0000',
                  fontSize: 11,
                  // backgroundColor: 'rgba(0,0,0,0.6)',
                  padding: [2, 4],
                  borderRadius: 2
                }
              });
            }
            if (t.alarmLow != null) {
              markLineData.push({
                yAxis: t.alarmLow,
                name: `${namePrefix}${level}下限`,
                lineStyle: { color: t.levelColor || phaseColor || '#ff0000', type: 'dashed', width: 2 },
                label: { 
                  show: true, 
                  position: 'end',
                  // formatter: `${level}下限: {c}V`,
                  formatter: `{c}V`,
                  color: t.levelColor || phaseColor || '#ff0000',
                  fontSize: 11,
                  // backgroundColor: 'rgba(0,0,0,0.6)',
                  padding: [2, 4],
                  borderRadius: 2
                }
              });
            }
          });
        }
        return markLineData;
      };

      // 生成各相位的阈值线，使用各相位对应的颜色
      // order: m1_a, m1_b, m1_c, m2_a, m2_b, m2_c
      const markLineDataList = [
        thresholds ? createMarkLineData(thresholds.m1_a, '#FACC15') : [],
        thresholds ? createMarkLineData(thresholds.m1_b, '#22C55E') : [],
        thresholds ? createMarkLineData(thresholds.m1_c, '#EF4444') : [],
        thresholds ? createMarkLineData(thresholds.m2_a, '#D97706') : [],
        thresholds ? createMarkLineData(thresholds.m2_b, '#059669') : [],
        thresholds ? createMarkLineData(thresholds.m2_c, '#DC2626') : []
      ];
      
      this.option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: function(params) {
            let result = params[0].name + '<br/>';
            params.forEach(item => {
              if (item.componentType === 'series') {
                result += item.marker + item.seriesName + ': ' + item.value + 'V<br/>';
              }
            });
            return result;
          }
        },
        legend: {
          data: names,
          textStyle: {
            color: '#fff',
            fontSize: 10
          },
          top: 8,
          left: 'center',
          itemWidth: 10,
          itemHeight: 8
        },
        grid: {
          left: '5%',
          // right: '25%',
          bottom: '10%',
          top: '25%', // Increased top due to larger legend
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: deviceNames,
          axisLine: {
            lineStyle: {
              color: 'rgba(73, 164, 243, .4)'
            }
          },
          axisLabel: {
            color: (value, index) => {
              const status = statuses && statuses[index];
              if (status !== undefined && status !== 0) {
                return '#ff0000';
              }
              return '#fff';
            },
            fontSize: 10,
            interval: 0,
            formatter: function(value) {
              if (value.length > 8) {
                return value.substring(0, 8) + '...';
              }
              return value;
            }
          },
          axisTick: {
            show: false
          }
        },
        yAxis: {
          type: 'value',
          name: '电压(V)',
          nameTextStyle: {
            color: '#fff',
            fontSize: 10
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: 'rgba(73, 164, 243, .4)'
            }
          },
          axisLabel: {
            color: '#fff',
            fontSize: 10
          },
          splitLine: {
            show: true,
            lineStyle: {
              type: 'dashed',
              color: 'rgba(73, 164, 243, .2)'
            }
          }
        },
        series: [
          // A路
          {
            name: names[0],
            type: 'bar',
            barWidth: '10%',
            barGap: '20%',
            data: m1_a_u,
            itemStyle: { color: '#FACC15', borderRadius: [2, 2, 0, 0] },
            markLine: markLineDataList[0].length > 0 ? { silent: true, symbol: 'none', data: markLineDataList[0] } : null
          },
          {
            name: names[1],
            type: 'bar',
            barWidth: '10%',
            data: m1_b_u,
            itemStyle: { color: '#22C55E', borderRadius: [2, 2, 0, 0] },
            markLine: markLineDataList[1].length > 0 ? { silent: true, symbol: 'none', data: markLineDataList[1] } : null
          },
          {
            name: names[2],
            type: 'bar',
            barWidth: '10%',
            data: m1_c_u,
            itemStyle: { color: '#EF4444', borderRadius: [2, 2, 0, 0] },
            markLine: markLineDataList[2].length > 0 ? { silent: true, symbol: 'none', data: markLineDataList[2] } : null
          },
          // B路 - Using simpler distinct colors or shades
          {
            name: names[3],
            type: 'bar',
            barWidth: '10%',
            data: m2_a_u,
            itemStyle: { color: '#D97706', borderRadius: [2, 2, 0, 0] }, // Darker Yellow
            markLine: markLineDataList[3].length > 0 ? { silent: true, symbol: 'none', data: markLineDataList[3] } : null
          },
          {
            name: names[4],
            type: 'bar',
            barWidth: '10%',
            data: m2_b_u,
            itemStyle: { color: '#059669', borderRadius: [2, 2, 0, 0] }, // Darker Green
            markLine: markLineDataList[4].length > 0 ? { silent: true, symbol: 'none', data: markLineDataList[4] } : null
          },
          {
            name: names[5],
            type: 'bar',
            barWidth: '10%',
            data: m2_c_u,
            itemStyle: { color: '#DC2626', borderRadius: [2, 2, 0, 0] }, // Darker Red
            markLine: markLineDataList[5].length > 0 ? { silent: true, symbol: 'none', data: markLineDataList[5] } : null
          }
        ]
      };
    }
  }
}
</script>

<style scoped>
</style>
